// /frontend/src/pages/checkout/DesignCheckoutPage.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import { toast } from 'react-toastify';
import { uploadService } from '../../services/uploadService';

// Helper function to convert base64 to blob for PDFs
const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const DesignCheckoutPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  
  const [designRequest, setDesignRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [wireTransferDetails, setWireTransferDetails] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    reference: ''
  });
  const [chequeDetails, setChequeDetails] = useState({
    payableTo: '',
    address: '',
    reference: ''
  });
  
  // Payment evidence upload state
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchDesignRequest = async () => {
    try {
      setLoading(true);
      const data = await uploadService.getDesignRequest(requestId);
      
      // Check if payment is already completed
      if (data.paymentStatus === 'paid') {
        toast.info('Payment has already been completed for this design request');
        navigate(`/custom-design/request/${requestId}`);
        return;
      }
      
      setDesignRequest(data);
      
      // Set reference numbers for payment methods
      setWireTransferDetails(prev => ({
        ...prev,
        bankName: 'Henderson National Bank',
        accountName: 'Henderson Design LLC',
        accountNumber: '9876543210',
        routingNumber: '123456789',
        reference: `DR-${data._id.substring(0, 8)}`
      }));
      
      setChequeDetails(prev => ({
        ...prev,
        payableTo: 'Henderson Design LLC',
        address: '123 Design Street, Suite 100, New York, NY 10001',
        reference: `DR-${data._id.substring(0, 8)}`
      }));
      
    } catch (err) {
      console.error('Error fetching design request:', err);
      setError(err.response?.data?.message || 'Failed to load design request details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId) {
      fetchDesignRequest();
    }
  }, [requestId, navigate]);

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleWireTransferSubmit = async () => {
    try {
      setProcessingPayment(true);
      
      // Update payment method in the database
      await uploadService.updatePaymentMethod(requestId, {
        method: 'wire_transfer',
        status: 'pending',
        reference: wireTransferDetails.reference,
        details: {
          ...wireTransferDetails
        }
      });
      
      toast.success('Wire transfer information submitted. Please upload payment evidence to expedite verification.');
      fetchDesignRequest();
    } catch (err) {
      toast.error('Error submitting wire transfer information');
      console.error('Error:', err);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleChequeSubmit = async () => {
    try {
      setProcessingPayment(true);
      
      // Update payment method in the database
      await uploadService.updatePaymentMethod(requestId, {
        method: 'cheque',
        status: 'pending',
        reference: chequeDetails.reference,
        details: {
          ...chequeDetails
        }
      });
      
      toast.success('Cheque information submitted. Please upload payment evidence to expedite verification.');
      fetchDesignRequest();
    } catch (err) {
      toast.error('Error submitting cheque information');
      console.error('Error:', err);
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Handle evidence file selection
  const handleEvidenceFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      // Check file type
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!acceptedTypes.includes(file.type)) {
        toast.error('Please upload a valid image or PDF file');
        return;
      }
      
      setEvidenceFile(file);
    }
  };
  
  // Handle evidence upload
  const handleEvidenceUpload = async () => {
    if (!evidenceFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setUploadingEvidence(true);
      
      const formData = new FormData();
      formData.append('paymentEvidence', evidenceFile);
      formData.append('paymentMethod', paymentMethod);
      
      // Upload payment evidence
      await uploadService.uploadPaymentEvidence(requestId, formData, (progress) => {
        setUploadProgress(progress);
      });
      
      toast.success('Payment evidence uploaded successfully');
      setEvidenceFile(null);
      setUploadProgress(0);
      
      // Refresh design request data
      fetchDesignRequest();
    } catch (error) {
      console.error('Error uploading payment evidence:', error);
      toast.error('Failed to upload payment evidence');
    } finally {
      setUploadingEvidence(false);
    }
  };

  // Function to view PDF from base64 data
  const viewPdf = (data, contentType) => {
    const blob = b64toBlob(data, contentType);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
          <Link to="/account/design-requests" className="text-teal-600 hover:text-teal-800">
            ← Back to Design Requests
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to={`/custom-design/request/${requestId}`} className="text-teal-600 hover:text-teal-800 mb-2 inline-block">
              ← Back to Design Request
            </Link>
            <h1 className="text-3xl font-bold">Complete Payment</h1>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold">Order Summary</h2>
          </div>
          <div className="px-6 py-4">
            <div className="flex justify-between py-3 border-b">
              <span className="font-medium">Design Fee:</span>
              <span>${designRequest.designFee}.00</span>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold">
              <span>Total:</span>
              <span>${designRequest.designFee}.00</span>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold">Payment Methods</h2>
          </div>
          <div className="p-6">
            {/* Payment Method Selection */}
            <div className="flex flex-wrap mb-8 gap-4">
              <button
                className={`flex-1 px-4 py-3 border rounded-md flex items-center justify-center gap-2 ${
                  paymentMethod === 'wire' ? 'border-teal-600 bg-teal-50' : 'border-gray-300'
                }`}
                onClick={() => handlePaymentMethodChange('wire')}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Wire Transfer</span>
              </button>
              <button
                className={`flex-1 px-4 py-3 border rounded-md flex items-center justify-center gap-2 ${
                  paymentMethod === 'cheque' ? 'border-teal-600 bg-teal-50' : 'border-gray-300'
                }`}
                onClick={() => handlePaymentMethodChange('cheque')}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Cheque</span>
              </button>
              <button
                className={`flex-1 px-4 py-3 border rounded-md flex items-center justify-center gap-2 ${
                  paymentMethod === 'card' ? 'border-teal-600 bg-teal-50' : 'border-gray-300'
                }`}
                onClick={() => handlePaymentMethodChange('card')}
                disabled
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span>Credit Card</span>
                <span className="text-xs text-gray-500 ml-1">(Coming Soon)</span>
              </button>
            </div>

            {/* Wire Transfer Payment */}
            {paymentMethod === 'wire' && (
            <div>
                <h3 className="font-medium mb-4">Wire Transfer Instructions</h3>
                <div className="bg-gray-50 p-6 rounded-md mb-6 space-y-4">
                <div className="flex justify-between">
                    <span className="text-gray-600">Bank Name:</span>
                    <span className="font-medium">{wireTransferDetails.bankName}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Account Name:</span>
                    <span className="font-medium">{wireTransferDetails.accountName}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Account Number:</span>
                    <span className="font-medium">{wireTransferDetails.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Routing Number:</span>
                    <span className="font-medium">{wireTransferDetails.routingNumber}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Reference/Memo:</span>
                    <span className="font-medium">{wireTransferDetails.reference}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">${designRequest.designFee}.00</span>
                </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-md mb-6">
                <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Please include your reference number in the memo field of your wire transfer. 
                    Upload payment evidence below to expedite verification.
                    We'll verify your payment (usually within 1-2 business days) and update your design request status.
                </p>
                </div>
                
                {/* Payment Evidence Upload - Always show this component */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-6">
                <h4 className="font-medium mb-2">Upload Payment Evidence</h4>
                <p className="text-sm text-gray-600 mb-4">
                    Upload a screenshot or PDF of your wire transfer confirmation to expedite processing.
                </p>
                
                <input
                    type="file"
                    id="payment-evidence-wire"
                    className="hidden"
                    onChange={handleEvidenceFileChange}
                    accept=".jpg,.jpeg,.png,.pdf"
                />
                
                {!evidenceFile ? (
                    <button
                    type="button"
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                    onClick={() => document.getElementById('payment-evidence-wire').click()}
                    >
                    Select File
                    </button>
                ) : (
                    <div className="mt-2">
                    <p className="text-sm font-medium">{evidenceFile.name}</p>
                    <p className="text-xs text-gray-500">{(evidenceFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    
                    {uploadingEvidence ? (
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                        <div 
                            className="bg-teal-600 h-2.5 rounded-full" 
                            style={{ width: `${uploadProgress}%` }}
                        ></div>
                        <p className="text-xs mt-1">Uploading: {uploadProgress}%</p>
                        </div>
                    ) : (
                        <div className="mt-2 flex space-x-2">
                        <button
                            type="button"
                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            onClick={() => setEvidenceFile(null)}
                        >
                            Change
                        </button>
                        <button
                            type="button"
                            className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                            onClick={handleEvidenceUpload}
                        >
                            Upload
                        </button>
                        </div>
                    )}
                    </div>
                )}
                </div>
                
                {/* Show this if evidence was uploaded successfully */}
                {designRequest.paymentEvidence && (
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                    <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <p className="text-green-800">Payment evidence uploaded successfully. Our team will verify your payment shortly.</p>
                    </div>
                    
                    {/* Show the uploaded evidence */}
                    <div className="mt-4">
                        <p className="text-sm font-medium mb-2">Uploaded evidence:</p>
                        
                        {designRequest.paymentEvidence.contentType && designRequest.paymentEvidence.contentType.startsWith('image/') ? (
                            <img 
                                src={`data:${designRequest.paymentEvidence.contentType};base64,${designRequest.paymentEvidence.data}`}
                                alt="Payment Evidence"
                                className="max-w-full h-auto rounded border"
                                style={{ maxHeight: '200px' }}
                            />
                        ) : designRequest.paymentEvidence.contentType === 'application/pdf' ? (
                            <button
                                onClick={() => viewPdf(designRequest.paymentEvidence.data, designRequest.paymentEvidence.contentType)}
                                className="mt-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                            >
                                View PDF Document
                            </button>
                        ) : (
                            <p className="text-sm text-gray-600">File uploaded successfully</p>
                        )}
                    </div>
                </div>
                )}
            
            </div>
            )}

            {/* Cheque Payment */}
            {paymentMethod === 'cheque' && (
            <div>
                <h3 className="font-medium mb-4">Cheque Payment Instructions</h3>
                <div className="bg-gray-50 p-6 rounded-md mb-6 space-y-4">
                <div className="flex justify-between">
                    <span className="text-gray-600">Make Payable To:</span>
                    <span className="font-medium">{chequeDetails.payableTo}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">${designRequest.designFee}.00</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-medium">{chequeDetails.reference}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-gray-600 mb-1">Mailing Address:</span>
                    <span className="font-medium">{chequeDetails.address}</span>
                </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-md mb-6">
                <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Please write your reference number in the memo field of your cheque. 
                    Upload a photo of your cheque below before mailing.
                    We'll verify your payment (usually within 5-7 business days after receipt) and update your design request status.
                </p>
                </div>
                
                {/* Payment Evidence Upload - Always show this component */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-6">
                <h4 className="font-medium mb-2">Upload Payment Evidence</h4>
                <p className="text-sm text-gray-600 mb-4">
                    Upload a photo of your cheque before mailing it to expedite processing.
                </p>
                
                <input
                    type="file"
                    id="payment-evidence-cheque"
                    className="hidden"
                    onChange={handleEvidenceFileChange}
                    accept=".jpg,.jpeg,.png,.pdf"
                />
                
                {!evidenceFile ? (
                    <button
                    type="button"
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                    onClick={() => document.getElementById('payment-evidence-cheque').click()}
                    >
                    Select File
                    </button>
                ) : (
                    <div className="mt-2">
                    <p className="text-sm font-medium">{evidenceFile.name}</p>
                    <p className="text-xs text-gray-500">{(evidenceFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    
                    {uploadingEvidence ? (
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                        <div 
                            className="bg-teal-600 h-2.5 rounded-full" 
                            style={{ width: `${uploadProgress}%` }}
                        ></div>
                        <p className="text-xs mt-1">Uploading: {uploadProgress}%</p>
                        </div>
                    ) : (
                        <div className="mt-2 flex space-x-2">
                        <button
                            type="button"
                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            onClick={() => setEvidenceFile(null)}
                        >
                            Change
                        </button>
                        <button
                            type="button"
                            className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                            onClick={handleEvidenceUpload}
                        >
                            Upload
                        </button>
                        </div>
                    )}
                    </div>
                )}
                </div>
                
                {/* Show this if evidence was uploaded successfully */}
                {designRequest.paymentEvidence && (
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                    <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <p className="text-green-800">Payment evidence uploaded successfully. Our team will verify your payment after receiving the cheque.</p>
                    </div>
                    
                    {/* Show the uploaded evidence */}
                    <div className="mt-4">
                        <p className="text-sm font-medium mb-2">Uploaded evidence:</p>
                        
                        {designRequest.paymentEvidence.contentType && designRequest.paymentEvidence.contentType.startsWith('image/') ? (
                            <img 
                                src={`data:${designRequest.paymentEvidence.contentType};base64,${designRequest.paymentEvidence.data}`}
                                alt="Payment Evidence"
                                className="max-w-full h-auto rounded border"
                                style={{ maxHeight: '200px' }}
                            />
                        ) : designRequest.paymentEvidence.contentType === 'application/pdf' ? (
                            <button
                                onClick={() => viewPdf(designRequest.paymentEvidence.data, designRequest.paymentEvidence.contentType)}
                                className="mt-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                            >
                                View PDF Document
                            </button>
                        ) : (
                            <p className="text-sm text-gray-600">File uploaded successfully</p>
                        )}
                    </div>
                </div>
                )}
            
            </div>
            )}

            {/* Credit Card Payment (Coming Soon) */}
            {paymentMethod === 'card' && (
              <div className="text-center py-8">
                <svg className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-xl font-medium mb-2">Credit Card Payments Coming Soon</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  We're currently working on implementing credit card payments. In the meantime, please use wire transfer or cheque payments.
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => handlePaymentMethodChange('wire')}
                    className="px-6 py-2 bg-teal-800 text-white rounded-md hover:bg-teal-900 transition-colors"
                  >
                    Choose Another Payment Method
                  </button>
                </div>
              </div>
            )}
            
            {/* Payment Success Information */}
            {designRequest.paymentEvidence && (
              <div className="mt-6 bg-green-50 border border-green-200 p-4 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <p className="text-green-800">
                    Payment evidence uploaded. Our team will verify your payment and update your design request status.
                  </p>
                </div>
                {designRequest.paymentEvidenceUploadedAt && (
                  <div className="mt-2">
                    <p className="text-sm text-green-700">Uploaded on: {new Date(designRequest.paymentEvidenceUploadedAt).toLocaleString()}</p>
                  </div>
                )}
                <div className="mt-4 text-center">
                  <Link 
                    to={`/custom-design/request/${requestId}`}
                    className="px-6 py-2 bg-teal-800 text-white rounded hover:bg-teal-900 inline-block"
                  >
                    View Design Request
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DesignCheckoutPage;